import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { switchMap, take } from "rxjs/operators";
import { EnvService } from "@gea-waldviertler-website/shop-lib";
import { CsrfService } from "../../csrf.service";

const CSRF_HEADER = 'X-XSRF-TOKEN'
const MUTATING_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE']

/*
 * Attaches the CSRF token as the "X-XSRF-TOKEN" header to state-changing
 * (mutating) gea-api requests. Mirrors the client half of Angular's HttpClient
 * XSRF mechanism, but works cross-site by sourcing the token from CsrfService
 * (a GET /customer/csrf) instead of a same-site cookie.
 *
 * The backend only *enforces* the token where it is annotated with
 * @RequireCsrfToken (currently only /customer/reset_password); sending the
 * header on other mutating requests is harmless and prepares them for future
 * enforcement.
 */
@Injectable()
export class HttpXsrfInterceptor implements HttpInterceptor {

  private envService = inject(EnvService)
  private csrfService = inject(CsrfService)

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isApiRequest =
      request.url.startsWith(this.envService.config.apiURL) ||
      request.url.startsWith(this.envService.config.apiURL_SSR)

    // only mutating gea-api requests; leave a pre-set header untouched
    if (! isApiRequest || ! MUTATING_METHODS.includes(request.method) || request.headers.has(CSRF_HEADER)) {
      return next.handle(request)
    }

    return this.csrfService.getToken().pipe(
      take(1),
      switchMap(token => {
        const req = token
          ? request.clone({ headers: request.headers.set(CSRF_HEADER, token) })
          : request
        return next.handle(req)
      })
    )
  }
}
